import React, { useState, useEffect, useRef } from 'react';
import { adminFetch } from '../../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

// ============================================================
// AiMatcherTab
// Usage: <AiMatcherTab user={user} />
// ============================================================

const AiMatcherTab = ({ user }) => {
  const [matcherOptions, setMatcherOptions] = useState({
    dryRun: true,
    model: 'gemini-3-pro-preview',
    allowedModels: [],
  });
  const [matchingRunning, setMatchingRunning] = useState(false);
  const [matchLogs, setMatchLogs] = useState([]);
  const logsEndRef = useRef(null);

  // ---- fetch config on mount ----

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await adminFetch(`${API_URL}/api/admin/config`);
        const result = await res.json();
        if (result.success && result.config?.ai) {
          const aiConfig = result.config.ai;
          setMatcherOptions((prev) => ({
            ...prev,
            model: aiConfig.matchingModel || prev.model,
            allowedModels: aiConfig.allowedMatchingModels || [],
          }));
        }
      } catch {
        // Config fetch failure is non-fatal; UI falls back to defaults
      }
    };
    fetchConfig();
  }, []);

  // ---- auto-scroll logs to bottom ----

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [matchLogs]);

  // ---- SSE matching handler ----

  // Read the SSE stream over fetch instead of EventSource: EventSource cannot
  // send the Authorization header, and putting the session token in the URL
  // would leak it into access logs.
  const startMatching = async () => {
    setMatchingRunning(true);
    setMatchLogs([]);

    const params = new URLSearchParams({
      dryRun: matcherOptions.dryRun,
      model: matcherOptions.model,
    });

    const handleEvent = (data) => {
      if (data.type === 'log') {
        setMatchLogs((prev) => [...prev, data.message]);
      } else if (data.type === 'done') {
        setMatchLogs((prev) => [...prev, `Process finished with code ${data.code}`]);
      } else if (data.type === 'error') {
        setMatchLogs((prev) => [...prev, `Error: ${data.message}`]);
      }
    };

    try {
      const res = await adminFetch(`${API_URL}/api/admin/run-matching?${params}`, {
        headers: { Accept: 'text/event-stream' },
      });
      if (!res.ok || !res.body) throw new Error(`Stream failed (${res.status})`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Frames are separated by a blank line; keep the trailing partial one.
        const frames = buffer.split('\n\n');
        buffer = frames.pop();
        for (const frame of frames) {
          const line = frame.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue; // ": keep-alive" heartbeat
          try {
            handleEvent(JSON.parse(line.slice(6)));
          } catch (e) {
            /* ignore malformed frame */
          }
        }
      }
    } catch (e) {
      setMatchLogs((prev) => [...prev, `Connection lost: ${e.message}`]);
    } finally {
      setMatchingRunning(false);
    }
  };

  // ---- render ----

  return (
    <div className="admin2-ai-matcher-tab">
      <h3 className="admin2-section-title">AI Matching Orchestrator</h3>

      {/* Settings row */}
      <div className="admin2-matcher-settings">
        <label className="admin2-checkbox-label">
          <input
            type="checkbox"
            checked={matcherOptions.dryRun}
            onChange={(e) =>
              setMatcherOptions({ ...matcherOptions, dryRun: e.target.checked })
            }
          />
          Dry Run (Simulation)
        </label>

        <label className="admin2-model-label">
          Model:
          <select
            className="admin2-select admin2-select-inline"
            value={matcherOptions.model}
            onChange={(e) =>
              setMatcherOptions({ ...matcherOptions, model: e.target.value })
            }
          >
            {matcherOptions.allowedModels && matcherOptions.allowedModels.length > 0 ? (
              matcherOptions.allowedModels.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))
            ) : (
              <>
                <option value="gemini-3-pro-preview">Gemini 3 Pro (Fallback)</option>
                <option value="gemini-1.5-pro-002">Gemini 1.5 Pro</option>
              </>
            )}
          </select>
        </label>

        <button
          className="admin2-btn-primary"
          onClick={startMatching}
          disabled={matchingRunning}
        >
          {matchingRunning ? (
            <>
              <span className="admin2-spinner-sm" />
              Running...
            </>
          ) : (
            'Start Matching Engine'
          )}
        </button>
      </div>

      {/* Log terminal */}
      <div className="admin2-log-terminal">
        {matchLogs.length === 0 ? (
          <span className="admin2-terminal-placeholder">Logs will appear here...</span>
        ) : (
          matchLogs.map((line, i) => (
            <div key={i} className="admin2-terminal-line">
              {line}
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
};

export default AiMatcherTab;
